import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, NavLink } from "react-router-dom";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import defaultAvatar from "../../../../assets/images/avatar.png";
import { REACT_APP_API_URL } from "../../../../env";
import EmployeeViewEditModal from "./EmployeeViewEditModal";

const AllEmpData = () => {

  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const normalizeDate = (date) => {
    if (!date) return "";

    const d = new Date(date);
    if (isNaN(d.getTime())) return "";

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const downloadExcel = () => {
    if (!employees.length) {
      alert("No employee data available to export");
      return;
    }

    // Format data for Excel
    const excelData = employees.map((emp, index) => ({
      "Sl No": index + 1,
      "Employee ID": emp.vchEmpCode || "",
      "Full Name": emp.vchFullName || "",
      "Designation": emp.vchDesignation || "",
      "Department/Unit": emp.vchDepartment || "",
      "Date of Joining": normalizeDate(emp.dtmDoj),
      "Date of Birth": normalizeDate(emp.dtmDob),
      "Email": emp.vchEmail || "",
      "Phone": emp.vchMobTel || "",
      "Gender": emp.vchGender || "",
      "Address": emp.vchPreAddress || "",
      "Company": emp.vchCompanyName || "",
    }));

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileData = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(fileData, "Employee_List.xlsx");
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(
        `${REACT_APP_API_URL}api/hrms/get-all-employee`,{withCredentials:true}
      );
      setEmployees(res.data);
    } catch (err) {
      setError("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (id, mode = "view") => {
    try {
      const res = await axios.get(
        `${REACT_APP_API_URL}api/hrms/get-employee/${id}`,
        {
          withCredentials: true,
        }
      );

      setSelectedEmployee(res.data);
      setEditMode(mode === "edit");
      setShowModal(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleApplyLeave = (emp) => {
    navigate("/hrms/leave-request", {
      state: { employee: emp },
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="card border-0 shadow-sm rounded-4 px-4 py-5 text-center">
          <div className="spinner-border text-primary mx-auto mb-3" role="status" aria-hidden="true"></div>
          <h5 className="mb-1">Loading employees</h5>
          <small className="text-muted">Please wait while we fetch the latest records.</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body">
          <div className="alert alert-danger mb-0 rounded-3">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="row g-3 g-xl-4">
      <div className="col-12">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 p-xl-4 bg-body-tertiary rounded-4 border">
          <div>
            <h3 className="fw-bold mb-1">Employee List</h3>
            <small className="text-muted">
              Total Employees: {employees.length}
            </small>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button
              className="btn btn-success d-inline-flex align-items-center"
              onClick={downloadExcel}
            >
              <i className="bi bi-file-earmark-excel me-1"></i>
              Download Excel
            </button>

            <NavLink
              to="/employees-add"
              className="btn btn-outline-dark d-inline-flex align-items-center"
              aria-label="Register Employee"
            >
              <i className="bi bi-person-plus me-1"></i>
              Add Employee
            </NavLink>
          </div>
        </div>
      </div>

      {employees.length === 0 ? (
        <div className="col-12">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body text-center py-5">
              <i className="bi bi-people fs-1 text-muted mb-3 d-block"></i>
              <h5 className="mb-2">No employees found</h5>
              <p className="text-muted mb-0">Register the first employee to start building the HRMS directory.</p>
            </div>
          </div>
        </div>
      ) : (
        employees.map((emp) => (
          <div
            key={emp.Id}
            className="col-xxl-3 col-xl-4 col-lg-4 col-md-6 col-sm-6"
          >
            <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-body p-4 d-flex flex-column">
                <div className="d-flex align-items-center">
                  <img
                    src={
                      emp.vchUserImage
                        ? `${REACT_APP_API_URL}${emp.vchUserImage.replace(/\\/g, "/")}`
                        : defaultAvatar
                    }
                    onError={(e) => (e.target.src = defaultAvatar)}
                    className="rounded-circle border flex-shrink-0"
                    style={{ width: "60px", height: "60px" }}
                    alt="Employee"
                  />
                  <div className="ms-3 flex-fill">
                    <h6 className="mb-1 fw-semibold text-truncate">{emp.vchFullName}</h6>
                    <span className="badge bg-light text-dark border fw-normal">{emp.vchEmpCode}</span>
                  </div>
                </div>

                <hr className="my-3" />

                <div className="d-flex flex-wrap gap-2 mb-3">
                  <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis border border-primary-subtle">
                    {emp.vchDesignation || 'No designation'}
                  </span>
                  <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle">
                    {emp.vchDepartment || 'No department'}
                  </span>
                </div>

                <div className="mt-auto d-grid gap-2 d-sm-flex">
                  <button
                    className="btn btn-sm btn-outline-primary flex-fill"
                    onClick={() => openModal(emp.Id, "view")}
                  >
                    View Details
                  </button>

                  <button
                    className="btn btn-sm btn-outline-success flex-fill"
                    onClick={() => handleApplyLeave(emp)}
                  >
                    Apply Leave
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      {showModal && (
        <EmployeeViewEditModal
          show={showModal}
          onClose={() => setShowModal(false)}
          employee={selectedEmployee}
          editMode={editMode}
          setEditMode={setEditMode}
          onSuccess={() => {
            setShowModal(false);
            fetchEmployees();
          }}
        />
      )}
    </div>
  );
};

export default AllEmpData;
